import { useTour } from "../contexts/TourContext";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";

const PAD = 8;       // gap between target and spotlight ring
const TOOLTIP_W = 360;
const TOOLTIP_GAP = 16;

// Decide where to place the tooltip relative to the target rect so it
// always stays within the viewport.
function computeTooltipPosition(rect, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Convert page-coordinate rect into viewport coordinates
  const top = rect.top - window.scrollY;
  const left = rect.left - window.scrollX;
  const right = left + rect.width;
  const bottom = top + rect.height;

  // Try preferred placement first, fall back if it doesn't fit
  const tryPlace = (p) => {
    if (p === "right" && right + TOOLTIP_GAP + TOOLTIP_W < vw) {
      return { left: right + TOOLTIP_GAP, top: Math.max(16, Math.min(top, vh - 240)) };
    }
    if (p === "left" && left - TOOLTIP_GAP - TOOLTIP_W > 0) {
      return { left: left - TOOLTIP_GAP - TOOLTIP_W, top: Math.max(16, Math.min(top, vh - 240)) };
    }
    if (p === "bottom" && bottom + TOOLTIP_GAP + 200 < vh) {
      return { left: Math.max(16, Math.min(left, vw - TOOLTIP_W - 16)), top: bottom + TOOLTIP_GAP };
    }
    if (p === "top" && top - TOOLTIP_GAP - 200 > 0) {
      return { left: Math.max(16, Math.min(left, vw - TOOLTIP_W - 16)), top: top - TOOLTIP_GAP - 220 };
    }
    return null;
  };

  return (
    tryPlace(placement) ||
    tryPlace("right") || tryPlace("bottom") || tryPlace("left") || tryPlace("top") ||
    // Last resort: center
    { left: Math.max(16, (vw - TOOLTIP_W) / 2), top: Math.max(16, (vh - 240) / 2) }
  );
}

export default function TourSpotlight() {
  const { active, stepIndex, totalSteps, currentStep, targetRect, next, prev, end } = useTour();
  if (!active || !currentStep) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const hasTarget = !!currentStep.target && !!targetRect;

  // Center intro/welcome card when there's no target.
  if (!hasTarget) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="tour-overlay">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative" data-testid="tour-card">
          <button onClick={end} data-testid="tour-skip" title="Skip tour"
            className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-500" />
          </button>
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
            Step {stepIndex + 1} of {totalSteps}
          </p>
          <h2 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-slate-900 mb-3" data-testid="tour-title">
            {currentStep.title}
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed font-['Manrope']" data-testid="tour-body">
            {currentStep.body}
          </p>
          <div className="flex items-center gap-2 mt-6">
            <button onClick={end} data-testid="tour-skip-bottom"
              className="text-xs font-medium text-slate-500 hover:text-slate-900 px-3 h-9">
              Skip
            </button>
            <div className="flex-1" />
            <button onClick={next} data-testid="tour-next"
              className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
              Start tour <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1 mt-5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all ${i === stepIndex ? "bg-rose-500 w-6" : "bg-slate-200 w-1.5"}`} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Spotlight cut-out: 4 dark rectangles surrounding the target.
  const r = targetRect;
  const top = r.top - window.scrollY - PAD;
  const left = r.left - window.scrollX - PAD;
  const width = r.width + PAD * 2;
  const height = r.height + PAD * 2;

  const tip = computeTooltipPosition(r, currentStep.placement || "right");

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="tour-overlay">
      {/* 4 dark slabs around the target form the cut-out — pointer events
          enabled so clicks outside the highlight are blocked. */}
      <div className="absolute bg-black/70 pointer-events-auto" style={{ top: 0, left: 0, right: 0, height: Math.max(0, top) }} />
      <div className="absolute bg-black/70 pointer-events-auto" style={{ top: top + height, left: 0, right: 0, bottom: 0 }} />
      <div className="absolute bg-black/70 pointer-events-auto" style={{ top, left: 0, width: Math.max(0, left), height }} />
      <div className="absolute bg-black/70 pointer-events-auto" style={{ top, left: left + width, right: 0, height }} />

      {/* Highlight ring around the target */}
      <div
        className="absolute rounded-xl ring-4 ring-rose-400/80 shadow-[0_0_0_4px_rgba(255,255,255,0.15)] pointer-events-none animate-tour-pulse"
        style={{ top, left, width, height }}
      />

      {/* Tooltip card */}
      <div
        className="absolute bg-white rounded-2xl shadow-2xl p-5 pointer-events-auto"
        style={{ top: tip.top, left: tip.left, width: TOOLTIP_W }}
        data-testid="tour-card"
      >
        <button onClick={end} data-testid="tour-skip" title="Skip tour"
          className="absolute top-3 right-3 w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center">
          <X className="w-3.5 h-3.5 text-slate-500" />
        </button>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-500 mb-1.5">
          Step {stepIndex + 1} of {totalSteps}
        </p>
        <h3 className="font-['Outfit'] text-lg font-semibold tracking-tight text-slate-900 pr-6" data-testid="tour-title">
          {currentStep.title}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed font-['Manrope'] mt-2" data-testid="tour-body">
          {currentStep.body}
        </p>

        <div className="flex items-center gap-2 mt-5">
          <button
            onClick={prev}
            disabled={isFirst}
            data-testid="tour-prev"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex-1 flex items-center justify-center gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all ${i === stepIndex ? "bg-rose-500 w-4" : "bg-slate-200 w-1.5"}`} />
            ))}
          </div>
          <button
            onClick={next}
            data-testid={isLast ? "tour-finish" : "tour-next"}
            className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
          >
            {isLast ? "Finish" : "Next"} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
